import React, { useEffect, useState, useRef } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { GALLERY } from "../lib/data";
import { useLang } from "../contexts/LanguageContext";

const BeforeAfter = ({ before, after, alt }) => {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const dragging = useRef(false);

  const setFromEvent = (e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const p = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setPos(p);
  };
  const onDown = (e) => {
    dragging.current = true;
    setFromEvent(e);
  };
  const onMove = (e) => {
    if (dragging.current) setFromEvent(e);
  };
  const onUp = () => {
    dragging.current = false;
  };

  useEffect(() => {
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchend", onUp);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchmove", onMove);
    return () => {
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchend", onUp);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onMove);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={ref}
      onMouseDown={onDown}
      onTouchStart={onDown}
      className="relative max-h-[88vh] max-w-[92vw] select-none overflow-hidden rounded-xl cursor-ew-resize"
      style={{ aspectRatio: "4 / 3" }}
      data-testid="before-after-slider"
    >
      <img src={after} alt={alt} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover pointer-events-none" />
      <div
        className="absolute inset-y-0 left-0 overflow-hidden pointer-events-none"
        style={{ width: `${pos}%` }}
      >
        <img
          src={before}
          alt={`${alt} before`}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ width: `${(100 / Math.max(pos, 0.01)) * 100}%` }}
        />
      </div>

      <div
        className="absolute inset-y-0 pointer-events-none"
        style={{ left: `${pos}%`, transform: "translateX(-50%)" }}
      >
        <div className="h-full w-0.5 bg-[color:var(--hd-gold)]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-[color:var(--hd-gold)] flex items-center justify-center shadow-xl">
          <ChevronLeft size={14} className="text-black -mr-1" />
          <ChevronRight size={14} className="text-black -ml-1" />
        </div>
      </div>

      <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-black/70 text-white text-xs font-display uppercase tracking-wider border border-white/10">
        Vorher
      </div>
      <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-[color:var(--hd-gold)]/90 text-black text-xs font-display uppercase tracking-wider font-bold">
        Nachher
      </div>
    </div>
  );
};

const Gallery = () => {
  const { t } = useLang();
  const [filter, setFilter] = useState("all");
  const [lightbox, setLightbox] = useState(null);

  const filtered =
    filter === "all" ? GALLERY : GALLERY.filter((i) => i.category === filter);

  useEffect(() => {
    const onKey = (e) => {
      if (lightbox === null) return;
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") setLightbox((i) => (i + 1) % filtered.length);
      if (e.key === "ArrowLeft")
        setLightbox((i) => (i - 1 + filtered.length) % filtered.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, filtered.length]);

  return (
    <section
      id="gallery"
      className="py-24 md:py-32 relative"
      data-testid="gallery-section"
    >
      <div className="container-hd">
        <div className="max-w-3xl mb-10">
          <div className="font-serif italic text-[color:var(--hd-gold)] text-sm md:text-base mb-3">
            — {t.gallery.overline}
          </div>
          <h2 className="font-display text-4xl md:text-6xl font-bold text-white tracking-tight">
            {t.gallery.title}
          </h2>
          <p className="text-white/60 mt-5 text-base md:text-lg max-w-2xl">
            {t.gallery.subtitle}
          </p>
        </div>

        <div className="flex items-center gap-2 mb-8" data-testid="gallery-filters">
          {[
            { id: "all", label: t.gallery.tabs.all },
            { id: "auto", label: t.gallery.tabs.auto },
            { id: "furniture", label: t.gallery.tabs.furniture },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setFilter(tab.id)}
              className={`px-5 py-2 rounded-full font-body text-sm uppercase tracking-wider transition-all ${
                filter === tab.id
                  ? "bg-[color:var(--hd-gold)] text-black"
                  : "bg-[#141414] text-white/60 border border-white/5 hover:border-[color:var(--hd-gold)]/30 hover:text-white"
              }`}
              data-testid={`gallery-filter-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="text-white/40 text-center py-20">—</div>
        ) : (
          <div
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 auto-rows-[180px] md:auto-rows-[220px] gap-3 md:gap-4"
            data-testid="gallery-grid"
          >
            {filtered.map((it, i) => {
              const spanPattern = [
                "col-span-2 row-span-2",
                "col-span-1 row-span-1",
                "col-span-1 row-span-2",
                "col-span-1 row-span-1",
                "col-span-2 row-span-1",
              ];
              const cls = spanPattern[i % spanPattern.length];
              return (
                <button
                  key={it.id}
                  onClick={() => setLightbox(i)}
                  className={`${cls} group relative overflow-hidden rounded-2xl border border-white/5 hover:border-[color:var(--hd-gold)]/50 transition-all`}
                  data-testid={`gallery-item-${i}`}
                >
                  <img
                    src={it.url}
                    alt={`${it.title || "Hetlav Detailing"} – ${it.category === "auto" ? "Autoaufbereitung & Innenreinigung" : "Polster- & Möbelreinigung"} Wien`}
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-60 group-hover:opacity-90 transition-opacity" />
                  {it.before && (
                    <div className="absolute top-3 right-3 font-display text-[10px] uppercase tracking-wider text-black bg-[color:var(--hd-gold)] px-2 py-0.5 rounded-full">
                      Vorher / Nachher
                    </div>
                  )}
                  <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
                    <div className="font-display text-sm text-white/90">
                      {it.title || "Hetlav"}
                    </div>
                    <div className="font-body text-[10px] uppercase tracking-wider text-[color:var(--hd-gold)] border border-[color:var(--hd-gold)]/40 px-2 py-0.5 rounded-full bg-black/40">
                      {it.category}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {lightbox !== null && filtered[lightbox] && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="gallery-lightbox"
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              setLightbox(null);
            }}
            className="absolute top-6 right-6 text-white/80 hover:text-[color:var(--hd-gold)] z-10"
            data-testid="lightbox-close"
            aria-label="Close"
          >
            <X size={32} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setLightbox((i) => (i - 1 + filtered.length) % filtered.length);
            }}
            className="absolute left-4 md:left-8 text-white/70 hover:text-[color:var(--hd-gold)]"
            data-testid="lightbox-prev"
            aria-label="Previous"
          >
            <ChevronLeft size={40} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setLightbox((i) => (i + 1) % filtered.length);
            }}
            className="absolute right-4 md:right-8 text-white/70 hover:text-[color:var(--hd-gold)]"
            data-testid="lightbox-next"
            aria-label="Next"
          >
            <ChevronRight size={40} />
          </button>
          <div onClick={(e) => e.stopPropagation()}>
            {filtered[lightbox].before ? (
              <BeforeAfter
                before={filtered[lightbox].before}
                after={filtered[lightbox].url}
                alt={`${filtered[lightbox].title} – Vorher/Nachher Autoaufbereitung Wien`}
              />
            ) : (
              <img
                src={filtered[lightbox].url}
                alt={`${filtered[lightbox].title} – Hetlav Detailing Wien`}
                loading="lazy"
                decoding="async"
                className="max-h-[88vh] max-w-[92vw] object-contain rounded-xl"
              />
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;
